// react-frontend/src/admin/dashboard/Dashboard.js
import React, { useState, useEffect } from 'react';
import { 
  Card, Row, Col, Spinner, Badge, 
  Tab, Nav, ProgressBar, Alert 
} from 'react-bootstrap';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { 
  FaCalendarAlt, FaCar, FaTicketAlt, FaUsers,
  FaCog, FaMoneyBillWave, FaMapMarkerAlt, FaBed,
  FaChartLine, FaTable, FaMapMarkedAlt, FaBook,
  FaUserShield, FaCubes, FaClock, FaPauseCircle
} from 'react-icons/fa';

// Charts
import { Bar, Line, Pie, Doughnut } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  LineElement,
  PointElement,
  ArcElement,
  Title,
  Tooltip,
  Legend,
  Colors
} from 'chart.js';

// Register Chart.js components
ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  LineElement,
  PointElement,
  ArcElement,
  Title,
  Tooltip,
  Legend,
  Colors
);

const Dashboard = () => {
  const [dashboardData, setDashboardData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [activeTab, setActiveTab] = useState('overview');
  const [timeRange, setTimeRange] = useState('week');

  useEffect(() => {
    const fetchDashboardData = async () => {
      try {
        setLoading(true);
        setError('');
        
        // Get the admin token from localStorage
        const token = localStorage.getItem('adminToken');
        
        const response = await axios.get(`${process.env.REACT_APP_API_URL}/admin/dashboard`, {
          headers: {
            'x-auth-token': token
          },
          params: {
            timeRange
          }
        });
        
        setDashboardData(response.data);
      } catch (err) {
        console.error('Failed to fetch dashboard data:', err);
        setError(err.response?.data?.error || 'Failed to load dashboard data. Please try again later.');
      } finally {
        setLoading(false);
      }
    };

    fetchDashboardData();
  }, [timeRange]);

  // Chart data configurations
  const bookingTrendData = {
    labels: dashboardData?.bookingTrends?.labels || [],
    datasets: [
      {
        label: 'Bookings',
        data: dashboardData?.bookingTrends?.data || [],
        backgroundColor: 'rgba(54, 162, 235, 0.6)',
        borderColor: 'rgba(54, 162, 235, 1)',
        borderWidth: 1,
        borderRadius: 6,
      },
    ],
  };

  const revenueTrendData = {
    labels: dashboardData?.revenueTrends?.labels || [],
    datasets: [
      {
        label: 'Revenue ($)',
        data: dashboardData?.revenueTrends?.data || [],
        backgroundColor: 'rgba(75, 192, 192, 0.6)',
        borderColor: 'rgba(75, 192, 192, 1)',
        borderWidth: 1,
        tension: 0.3,
      },
    ],
  };

  const bookingStatusData = {
    labels: ['Pending', 'Confirmed', 'Completed', 'Cancelled'],
    datasets: [
      {
        data: [
          dashboardData?.bookingStatus?.pending || 0,
          dashboardData?.bookingStatus?.confirmed || 0,
          dashboardData?.bookingStatus?.completed || 0,
          dashboardData?.bookingStatus?.cancelled || 0,
        ],
        backgroundColor: [
          'rgba(255, 206, 86, 0.7)',
          'rgba(54, 162, 235, 0.7)',
          'rgba(75, 192, 192, 0.7)',
          'rgba(255, 99, 132, 0.7)',
        ],
        borderColor: [
          'rgba(255, 206, 86, 1)',
          'rgba(54, 162, 235, 1)',
          'rgba(75, 192, 192, 1)',
          'rgba(255, 99, 132, 1)',
        ],
        borderWidth: 1,
      },
    ],
  };

  const vehiclePopularityData = {
    labels: dashboardData?.vehiclePopularity?.map(v => v.Vehicle_Name) || [],
    datasets: [
      {
        label: 'Bookings',
        data: dashboardData?.vehiclePopularity?.map(v => v.bookingCount) || [],
        backgroundColor: [
          'rgba(255, 99, 132, 0.7)',
          'rgba(54, 162, 235, 0.7)',
          'rgba(255, 206, 86, 0.7)',
          'rgba(75, 192, 192, 0.7)',
        ],
        borderColor: [
          'rgba(255, 99, 132, 1)',
          'rgba(54, 162, 235, 1)',
          'rgba(255, 206, 86, 1)',
          'rgba(75, 192, 192, 1)',
        ],
        borderWidth: 1,
      },
    ],
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'pending': return <Badge bg="warning">Pending</Badge>;
      case 'confirmed': return <Badge bg="primary">Confirmed</Badge>;
      case 'completed': return <Badge bg="success">Completed</Badge>;
      case 'cancelled': return <Badge bg="danger">Cancelled</Badge>;
      default: return <Badge bg="secondary">Unknown</Badge>;
    }
  };

  const handleTimeRangeChange = (range) => {
    setTimeRange(range);
  };

  if (loading && !dashboardData) {
    return (
      <div className="d-flex justify-content-center align-items-center" style={{ height: '60vh' }}>
        <Spinner animation="border" variant="primary" />
      </div>
    );
  }

  if (error) {
    return (
      <Alert variant="danger" className="mt-4">
        {error}
      </Alert>
    );
  }

  return (
    <div className="dashboard-container">
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h2>Admin Dashboard</h2>
        <div className="btn-group">
          <button 
            className={`btn btn-sm ${timeRange === 'day' ? 'btn-primary' : 'btn-outline-primary'}`}
            onClick={() => handleTimeRangeChange('day')}
          >
            Today
          </button>
          <button 
            className={`btn btn-sm ${timeRange === 'week' ? 'btn-primary' : 'btn-outline-primary'}`}
            onClick={() => handleTimeRangeChange('week')}
          >
            Week
          </button>
          <button 
            className={`btn btn-sm ${timeRange === 'month' ? 'btn-primary' : 'btn-outline-primary'}`}
            onClick={() => handleTimeRangeChange('month')}
          >
            Month
          </button>
        </div>
      </div>

      <Tab.Container activeKey={activeTab} onSelect={(k) => setActiveTab(k)}>
        <Nav variant="tabs" className="mb-4">
          <Nav.Item>
            <Nav.Link eventKey="overview">
              <FaChartLine className="me-2" />
              Overview
            </Nav.Link>
          </Nav.Item>
          <Nav.Item>
            <Nav.Link eventKey="analytics">
              <FaTable className="me-2" />
              Analytics
            </Nav.Link>
          </Nav.Item>
          <Nav.Item>
            <Nav.Link eventKey="quickAccess">
              <FaMapMarkedAlt className="me-2" />
              Quick Access
            </Nav.Link>
          </Nav.Item>
        </Nav>

        <Tab.Content>
          <Tab.Pane eventKey="overview">
            {/* Summary Cards */}
            <Row className="g-4 mb-4">
              <Col md={6} lg={3}>
                <Card className="h-100 shadow-sm">
                  <Card.Body>
                    <div className="d-flex justify-content-between align-items-center">
                      <div>
                        <h6 className="text-muted mb-2">Total Bookings</h6>
                        <h3 className="mb-0">{dashboardData?.totalBookings || 0}</h3>
                        <small className={dashboardData?.bookingChange >= 0 ? 'text-success' : 'text-danger'}>
                          {dashboardData?.bookingChange >= 0 ? '+' : ''}{dashboardData?.bookingChange || 0}% from last {timeRange}
                        </small>
                      </div>
                      <div className="bg-primary bg-opacity-10 p-3 rounded">
                        <FaCalendarAlt className="text-primary" size={24} />
                      </div>
                    </div>
                  </Card.Body>
                </Card>
              </Col>

              <Col md={6} lg={3}>
                <Card className="h-100 shadow-sm">
                  <Card.Body>
                    <div className="d-flex justify-content-between align-items-center">
                      <div>
                        <h6 className="text-muted mb-2">Total Revenue</h6>
                        <h3 className="mb-0">${(dashboardData?.totalRevenue || 0).toLocaleString()}</h3>
                        <small className={dashboardData?.revenueChange >= 0 ? 'text-success' : 'text-danger'}>
                          {dashboardData?.revenueChange >= 0 ? '+' : ''}{dashboardData?.revenueChange || 0}% from last {timeRange}
                        </small>
                      </div>
                      <div className="bg-success bg-opacity-10 p-3 rounded">
                        <FaMoneyBillWave className="text-success" size={24} />
                      </div>
                    </div>
                  </Card.Body>
                </Card>
              </Col>

              <Col md={6} lg={3}>
                <Card className="h-100 shadow-sm">
                  <Card.Body>
                    <div className="d-flex justify-content-between align-items-center">
                      <div>
                        <h6 className="text-muted mb-2">Active Users</h6>
                        <h3 className="mb-0">{dashboardData?.activeUsers || 0}</h3>
                        <small className={dashboardData?.userChange >= 0 ? 'text-success' : 'text-danger'}>
                          {dashboardData?.userChange >= 0 ? '+' : ''}{dashboardData?.userChange || 0}% from last {timeRange}
                        </small>
                      </div>
                      <div className="bg-info bg-opacity-10 p-3 rounded">
                        <FaUsers className="text-info" size={24} />
                      </div>
                    </div>
                  </Card.Body>
                </Card>
              </Col>

              <Col md={6} lg={3}>
                <Card className="h-100 shadow-sm">
                  <Card.Body>
                    <div className="d-flex justify-content-between align-items-center">
                      <div>
                        <h6 className="text-muted mb-2">Pending Bookings</h6>
                        <h3 className="mb-0">{dashboardData?.pendingBookings || 0}</h3>
                        <small className="text-danger">
                          {dashboardData?.pendingChange > 0 ? '+' : ''}{dashboardData?.pendingChange || 0} from yesterday
                        </small>
                      </div>
                      <div className="bg-warning bg-opacity-10 p-3 rounded">
                        <FaClock className="text-warning" size={24} />
                      </div>
                    </div>
                  </Card.Body>
                </Card>
              </Col>
            </Row>

            {/* Charts Row */}
            <Row className="g-4 mb-4">
              <Col lg={6}>
                <Card className="h-100 shadow-sm">
                  <Card.Body>
                    <Card.Title>Booking Trends</Card.Title>
                    <div style={{ height: '300px' }}>
                      <Bar 
                        data={bookingTrendData} 
                        options={{
                          responsive: true,
                          maintainAspectRatio: false,
                          plugins: {
                            legend: {
                              position: 'top',
                            },
                          },
                        }}
                      />
                    </div>
                  </Card.Body>
                </Card>
              </Col>

              <Col lg={6}>
                <Card className="h-100 shadow-sm">
                  <Card.Body>
                    <Card.Title>Revenue Trends</Card.Title>
                    <div style={{ height: '300px' }}>
                      <Line 
                        data={revenueTrendData} 
                        options={{
                          responsive: true,
                          maintainAspectRatio: false,
                          plugins: {
                            legend: {
                              position: 'top',
                            },
                          },
                        }}
                      />
                    </div>
                  </Card.Body>
                </Card>
              </Col>
            </Row>

            {/* Second Charts Row */}
            <Row className="g-4">
              <Col md={6}>
                <Card className="h-100 shadow-sm">
                  <Card.Body>
                    <Card.Title>Booking Status</Card.Title>
                    <div style={{ height: '250px' }}>
                      <Doughnut 
                        data={bookingStatusData} 
                        options={{
                          responsive: true,
                          maintainAspectRatio: false,
                          plugins: {
                            legend: {
                              position: 'right',
                            },
                          },
                        }}
                      />
                    </div>
                  </Card.Body>
                </Card>
              </Col>

              <Col md={6}>
                <Card className="h-100 shadow-sm">
                  <Card.Body>
                    <Card.Title>Vehicle Popularity</Card.Title>
                    <div style={{ height: '250px' }}>
                      <Pie 
                        data={vehiclePopularityData} 
                        options={{
                          responsive: true,
                          maintainAspectRatio: false,
                          plugins: {
                            legend: {
                              position: 'right',
                            },
                          },
                        }}
                      />
                    </div>
                  </Card.Body>
                </Card>
              </Col>
            </Row>
          </Tab.Pane>

          <Tab.Pane eventKey="analytics">
            <Row className="g-4">
              <Col lg={8}>
                <Card className="shadow-sm">
                  <Card.Body>
                    <Card.Title>Recent Bookings</Card.Title>
                    <div className="table-responsive">
                      <table className="table table-hover">
                        <thead>
                          <tr>
                            <th>Booking ID</th>
                            <th>Customer</th>
                            <th>Date</th>
                            <th>Amount</th>
                            <th>Status</th>
                            <th>Vehicle</th>
                          </tr>
                        </thead>
                        <tbody>
                          {dashboardData?.recentBookings?.map((booking) => (
                            <tr key={booking._id}>
                              <td>{booking._id.toString().substring(0, 8)}...</td>
                              <td>{booking.customerName}</td>
                              <td>{new Date(booking.pickupDate).toLocaleDateString()}</td>
                              <td>${booking.totalAmount}</td>
                              <td>{getStatusBadge(booking.bookingStatus)}</td>
                              <td>{booking.vehicleName}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </Card.Body>
                </Card>
              </Col>

              <Col lg={4}>
                <Card className="shadow-sm">
                  <Card.Body>
                    <Card.Title>System Overview</Card.Title>
                    
                    <div className="mb-4">
                      <div className="d-flex justify-content-between mb-1">
                        <span>Active Vehicles</span>
                        <span>{dashboardData?.activeVehicles || 0}</span>
                      </div>
                      <ProgressBar 
                        now={(dashboardData?.activeVehicles / dashboardData?.totalVehicles) * 100 || 0} 
                        variant="primary" 
                      />
                    </div>

                    <div className="mb-4">
                      <div className="d-flex justify-content-between mb-1">
                        <span>Active Coupons</span>
                        <span>{dashboardData?.activeCoupons || 0}</span>
                      </div>
                      <ProgressBar 
                        now={(dashboardData?.activeCoupons / dashboardData?.totalCoupons) * 100 || 0} 
                        variant="success" 
                      />
                    </div>

                    <div className="mb-4">
                      <div className="d-flex justify-content-between mb-1">
                        <span>Paused Dates</span>
                        <span>{dashboardData?.pausedDates || 0}</span>
                      </div>
                      <ProgressBar 
                        now={dashboardData?.pausedDates > 0 ? 100 : 0} 
                        variant="warning" 
                      />
                    </div>

                    <div className="mb-4">
                      <div className="d-flex justify-content-between mb-1">
                        <span>Overcharge Locations</span>
                        <span>{dashboardData?.overchargeLocations || 0}</span>
                      </div>
                      <ProgressBar 
                        now={dashboardData?.overchargeLocations > 0 ? 100 : 0} 
                        variant="info" 
                      />
                    </div>
                  </Card.Body>
                </Card>
              </Col>
            </Row>
          </Tab.Pane>

          <Tab.Pane eventKey="quickAccess">
            <Row className="g-4">
              {/* Bookings Management */}
              <Col md={6} lg={4}>
                <Card className="h-100 shadow-sm">
                  <Card.Body className="text-center">
                    <div className="bg-primary bg-opacity-10 p-3 rounded-circle d-inline-block mb-3">
                      <FaBook className="text-primary" size={24} />
                    </div>
                    <Card.Title>Bookings Management</Card.Title>
                    <Card.Text className="text-muted">
                      {dashboardData?.totalBookings || 0} total bookings
                    </Card.Text>
                    <div className="d-grid gap-2">
                      <Link to="/admin/bookings" className="btn btn-primary">
                        Go to Bookings
                      </Link>
                    </div>
                  </Card.Body>
                </Card>
              </Col>

              {/* Vehicle Management */}
              <Col md={6} lg={4}>
                <Card className="h-100 shadow-sm">
                  <Card.Body className="text-center">
                    <div className="bg-success bg-opacity-10 p-3 rounded-circle d-inline-block mb-3">
                      <FaCar className="text-success" size={24} />
                    </div>
                    <Card.Title>Vehicle Management</Card.Title>
                    <Card.Text className="text-muted">
                      {dashboardData?.totalVehicles || 0} vehicles available
                    </Card.Text>
                    <div className="d-grid gap-2">
                      <Link to="/admin/vehicles" className="btn btn-success">
                        Go to Vehicles
                      </Link>
                    </div>
                  </Card.Body>
                </Card>
              </Col>

              {/* User Management */}
              <Col md={6} lg={4}>
                <Card className="h-100 shadow-sm">
                  <Card.Body className="text-center">
                    <div className="bg-info bg-opacity-10 p-3 rounded-circle d-inline-block mb-3">
                      <FaUserShield className="text-info" size={24} />
                    </div>
                    <Card.Title>User Management</Card.Title>
                    <Card.Text className="text-muted">
                      {dashboardData?.totalUsers || 0} registered users
                    </Card.Text>
                    <div className="d-grid gap-2">
                      <Link to="/admin/users" className="btn btn-info">
                        Go to Users
                      </Link>
                    </div>
                  </Card.Body>
                </Card>
              </Col>

              {/* Coupon Management */}
              <Col md={6} lg={4}>
                <Card className="h-100 shadow-sm">
                  <Card.Body className="text-center">
                    <div className="bg-warning bg-opacity-10 p-3 rounded-circle d-inline-block mb-3">
                      <FaTicketAlt className="text-warning" size={24} />
                    </div>
                    <Card.Title>Coupon Management</Card.Title>
                    <Card.Text className="text-muted">
                      {dashboardData?.totalCoupons || 0} coupons in system
                    </Card.Text>
                    <div className="d-grid gap-2">
                      <Link to="/admin/coupons" className="btn btn-warning">
                        Go to Coupons
                      </Link>
                    </div>
                  </Card.Body>
                </Card>
              </Col>

              {/* System Settings */}
              <Col md={6} lg={4}>
                <Card className="h-100 shadow-sm">
                  <Card.Body className="text-center">
                    <div className="bg-secondary bg-opacity-10 p-3 rounded-circle d-inline-block mb-3">
                      <FaCog className="text-secondary" size={24} />
                    </div>
                    <Card.Title>System Settings</Card.Title>
                    <Card.Text className="text-muted">
                      Configure all system parameters
                    </Card.Text>
                    <div className="d-grid gap-2">
                      <Link to="/admin/settings" className="btn btn-secondary">
                        Go to Settings
                      </Link>
                    </div>
                  </Card.Body>
                </Card>
              </Col>

              {/* Addons Management */}
              <Col md={6} lg={4}>
                <Card className="h-100 shadow-sm">
                  <Card.Body className="text-center">
                    <div className="bg-danger bg-opacity-10 p-3 rounded-circle d-inline-block mb-3">
                      <FaCubes className="text-danger" size={24} />
                    </div>
                    <Card.Title>Addons Management</Card.Title>
                    <Card.Text className="text-muted">
                      {dashboardData?.totalAddons || 0} addons available
                    </Card.Text>
                    <div className="d-grid gap-2">
                      <Link to="/admin/settings/addons" className="btn btn-danger">
                        Go to Addons
                      </Link>
                    </div>
                  </Card.Body>
                </Card>
              </Col>
            </Row>
          </Tab.Pane>
        </Tab.Content>
      </Tab.Container>
    </div>
  );
};

export default Dashboard;